/**
 * DeadLetterTab Component
 *
 * Lists messages sitting in the RabbitMQ dead-letter queue — messages that failed
 * processing after exhausting their retries. Shows payload, the publishing service
 * ("who published it"), and the failure reason.
 */

import { useState, useMemo, useCallback } from 'react';
import { IconSearch, IconEye, IconRefresh, IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  TextInput,
  Badge,
  Text,
  Code,
  ActionIcon,
  Modal,
  Alert,
} from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import type { DeadLetterTabProps, DeadLetterMessage } from '../types';

type DeadLetterRow = DeadLetterMessage & { id: string };

const formatTimestamp = (timestamp?: string | null): string => {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const prettyJson = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

export function DeadLetterTab({ messages, available, loadError, onRefresh, loading }: DeadLetterTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<DeadLetterMessage | null>(null);

  const rows: DeadLetterRow[] = useMemo(
    () => messages.map((m, i) => ({ ...m, id: m.messageId ?? `dlq-${i}` })),
    [messages]
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter(
      (m) =>
        m.messageType.toLowerCase().includes(query) ||
        m.publishedBy.toLowerCase().includes(query) ||
        (m.deathQueue ?? '').toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);

  const columns: DataTableColumn<DeadLetterRow>[] = useMemo(
    () => [
      {
        key: 'messageType',
        label: 'Message Type',
        sortable: true,
        render: (value) => (
          <Badge color="blue" variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'publishedBy',
        label: 'Published By',
        sortable: true,
        render: (value) => <Text size="sm">{value}</Text>,
      },
      {
        key: 'deathReason',
        label: 'Failure',
        render: (value) => (
          <Badge color="red" variant="light">
            {value || 'unknown'}
          </Badge>
        ),
      },
      {
        key: 'deathQueue',
        label: 'Original Queue',
        sortable: true,
        render: (value) => (
          <Text size="sm" c="dimmed">
            {value || '—'}
          </Text>
        ),
      },
      {
        key: 'deathCount',
        label: 'Attempts',
        sortable: true,
        render: (value) => (
          <Badge color="gray" variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'publishedAt',
        label: 'Published',
        sortable: true,
        render: (value) => (
          <Text size="sm" c="dimmed">
            {formatTimestamp(value)}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, row) => (
          <ActionIcon variant="subtle" onClick={() => setSelected(row)} aria-label="View message">
            <IconEye size={16} />
          </ActionIcon>
        ),
      },
    ],
    []
  );

  const refreshButton = (
    <Button variant="subtle" leftSection={<IconRefresh size={16} />} onClick={onRefresh} loading={loading}>
      Refresh
    </Button>
  );

  // The Management API could not be reached.
  if (!available) {
    return (
      <Stack gap="md">
        <Group justify="flex-end">{refreshButton}</Group>
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Dead-letter queue unavailable">
          {loadError ||
            'Could not reach the RabbitMQ Management API. Ensure the rabbitmq_management plugin is enabled and the CCMS.Api.System service can reach the broker.'}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <TextInput
          placeholder="Search by type, publisher or queue..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1, maxWidth: 380 }}
        />
        {refreshButton}
      </Group>

      {messages.length === 0 ? (
        <Alert color="green" icon={<IconCircleCheck size={16} />} title="No failed messages">
          The dead-letter queue is empty — every message has been processed successfully.
        </Alert>
      ) : (
        <DataTable columns={columns} data={filteredRows} striped highlightOnHover />
      )}

      <Modal
        opened={selected !== null}
        onClose={() => setSelected(null)}
        title="Dead-lettered message"
        size="xl"
      >
        {selected && (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge color="blue" variant="light">
                {selected.messageType}
              </Badge>
              <Badge color="red" variant="light">
                {selected.deathReason || 'failed'}
              </Badge>
              {selected.deathCount > 0 && (
                <Badge color="gray" variant="light">
                  {selected.deathCount} attempt{selected.deathCount === 1 ? '' : 's'}
                </Badge>
              )}
            </Group>

            <Text size="sm">
              Published by{' '}
              <Text span fw={600}>
                {selected.publishedBy}
              </Text>{' '}
              · {formatTimestamp(selected.publishedAt)}
            </Text>

            {selected.deathQueue && (
              <Text size="sm" c="dimmed">
                Originally failed in queue <Code>{selected.deathQueue}</Code>
              </Text>
            )}
            {selected.messageId && (
              <Text size="xs" c="dimmed">
                Message ID: <Code>{selected.messageId}</Code>
              </Text>
            )}
            {selected.correlationId && (
              <Text size="xs" c="dimmed">
                Correlation: <Code>{selected.correlationId}</Code>
              </Text>
            )}
            <Text size="xs" c="dimmed">
              Routing: <Code>{selected.exchange || '(default)'}</Code> → <Code>{selected.routingKey}</Code>
            </Text>

            <Text size="sm" fw={600}>
              Payload ({selected.payloadBytes.toLocaleString()} bytes)
            </Text>
            <Code block>{prettyJson(selected.payload)}</Code>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
